import { IResponseProcessor } from "..";
import { IDocument } from "../../stores/document/Document";
import { IResellerInfo } from "../reseller/ResellersApi";

export default class PurchaseOrdersApi {
  constructor(
    private _baseUrl: string,
    private _getToken: (applicationId: string) => string,
    private _processor: IResponseProcessor
  ) {
    console.warn(
      "Deprecation Warning: The legacy 'PurchaseOrdersApi' class is in use. Please migrate to RTK Query hooks from randmar-api-client as per CONTRIBUTING.md."
    );
  }

  async retrieveOpenOrders(applicationId: string): Promise<IOrderDetailResult[]> {
    return this._getAndJson<IOrderDetailResult[]>(applicationId, "/Documents/ActiveOrderDetails");
  }

  async retrieveCompletedOrders(applicationId: string): Promise<IOrderDetailResult[]> {
    return this._getAndJson<IOrderDetailResult[]>(applicationId, "/Documents/CompletedOrderDetails");
  }

  async retrieveRequisitions(applicationId: string): Promise<IRequisitionResult[]> {
    return this._getAndJson<IRequisitionResult[]>(applicationId, "/Documents/Requisitions");
  }

  async retrievePurchaseOrders(applicationId: string): Promise<IPurchaseOrderResult[]> {
    return this._getAndJson<IPurchaseOrderResult[]>(applicationId, "/Documents/PurchaseOrders");
  }

  async retrieveReceipts(applicationId: string): Promise<IReceiptResult[]> {
    return this._getAndJson<IReceiptResult[]>(applicationId, "/Documents/Receipts");
  }

  async retrieveInvoices(applicationId: string): Promise<IInvoice[]> {
    return this._getAndJson<IInvoice[]>(applicationId, "/Documents/Invoices");
  }
  
  async vendorInvoice(applicationId: string, receiptNumber: string, invoiceNumber: string, invoiceDate: string, invoiceTotal: number): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Invoice/${receiptNumber}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${this._getToken(applicationId)}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        InvoiceNumber: invoiceNumber,
        InvoiceDate: invoiceDate,
        InvoiceTotal: invoiceTotal
      })
    });

    const success: boolean = await res.json();

    return success;
  }

  async retrieveGeneralDocument(applicationId: string, orderNumber: string): Promise<IGeneralDocument> {
    return this._getAndJson<IGeneralDocument>(applicationId, `/Document/SalesOrder/${orderNumber}`);
  }

  async retrieveDocument(applicationId: string, documentNumber: string): Promise<IDocument> {
    return this._getAndJson<IDocument>(applicationId, `/Document/${documentNumber}`);
  }

  async regenerateDraftPurchases(applicationId: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/DraftPurchases`, {
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` },
      method: "PUT"
    });

    this._processor.process(res);

    const success: boolean = await res.json();

    return success;
  }

  async sendPurchaseOrderToManufacturer(applicationId: string, documentNumber: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/PurchaseOrder/${documentNumber}/Send`, {
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` },
      method: "PUT"
    });

    this._processor.process(res);

    const success: boolean = await res.json();

    return success;
  }

  async retrieveDocumentAsPDF(applicationId: string, documentNumber: string): Promise<any> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Document/${documentNumber}/PDF`, {
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    const result: any = await res.blob();

    return result;
  }

  async sendDocumentEmail(applicationId: string, documentNumber: string, email: string, comment: string): Promise<boolean> {
    const url = new URL(`${this._baseUrl}/${applicationId}/Document/${documentNumber}/Email`);
    url.searchParams.set("emailaddress", email);
    if (comment) {
      url.searchParams.set("comment", comment);
    }

    const res = await fetch(url.toString(), {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return await res.json();
  }

  async createPurchaseOrder(applicationId: string, requisitions: string[], sendToManufacturer: boolean): Promise<string> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/PurchaseOrder?sendToManufacturer=${sendToManufacturer}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${this._getToken(applicationId)}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requisitions)
    });

    return await res.text();
  }

  async cancelPurchaseOrder(applicationId: string, purchaseOrderNumber: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/PurchaseOrder/${purchaseOrderNumber}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return await res.json();
  }

  async cancelRequisition(applicationId: string, requisitionNumber: string): Promise<boolean> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}/Requisition/${requisitionNumber}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return await res.json();
  }

  async editExpectedDate(applicationId: string, purchaseOrderNumber: string, newDate: string): Promise<boolean> {
    const url = new URL(`${this._baseUrl}/${applicationId}/PurchaseOrder/${purchaseOrderNumber}/EditExpectedDate`);
    url.searchParams.set("newValue", newDate);

    const res = await fetch(url.toString(), {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return res.ok;
  }

  async editPartNumberPurchaseOrder(applicationId: string, purchaseOrderNumber: string, randmarSKU: string, newQuantity: number): Promise<boolean> {
    const url = new URL(`${this._baseUrl}/${applicationId}/PurchaseOrder/${purchaseOrderNumber}/EditItem/${encodeURIComponent(randmarSKU)}`);
    url.searchParams.set("quantity", String(newQuantity));

    const res = await fetch(url.toString(), {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return res.ok;
  }

  async editPartNumberUnitPrice(applicationId: string, purchaseOrderNumber: string, randmarSKU: string, newUnitPrice: number, editStandardCost: boolean): Promise<boolean> {
    const url = new URL(`${this._baseUrl}/${applicationId}/PurchaseOrder/${purchaseOrderNumber}/EditItem/${encodeURIComponent(randmarSKU)}`);
    url.searchParams.set("unitPrice", String(newUnitPrice));
    url.searchParams.set("editStandardCost", String(editStandardCost));

    const res = await fetch(url.toString(), {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return res.ok;
  }

  async editPartNumberRequisition(applicationId: string, requisitionNumber: string, randmarSKU: string, newQuantity: number): Promise<boolean> {
    const url = new URL(`${this._baseUrl}/${applicationId}/Requisition/${requisitionNumber}/EditItem/${encodeURIComponent(randmarSKU)}`);
    url.searchParams.set("quantity", String(newQuantity));

    const res = await fetch(url.toString(), {
      method: "POST",
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return res.ok;
  }

  private async _getAndJson<T>(applicationId: string, path: string): Promise<T> {
    const res = await fetch(
      `${this._baseUrl}/${applicationId}${path}`, {
      headers: { Authorization: `Bearer ${this._getToken(applicationId)}` }
    });

    this._processor.process(res);

    return res.json();
  }
}

export interface IRequisitionResult {
  RequisitionNumber: string;
  VendorName: string;
  RequestedBy: string;
  RequisitionDate: number;
  Location: string;
  Reference: string;
  PartNumbers: IRequisitionPartNumberResult[];
  SubTotal: number;
  ManufacturerId: string;
  ManufacturerName: string;
  RequiredByDate: number;
}

export interface IPurchaseOrderResult {
  BillToAddress1: string;
  BillToAddress2: string;
  BillToCity: string;
  BillToName: string;
  BillToPostalCode: string;
  BillToProvince: string;
  Comments: string;
  ContactEmail: string;
  ExpectedArrivalDate: number;
  Location: string;
  ManufacturerId: string;
  ManufacturerName: string;
  PurchaseOrderDate: number;
  PurchaseOrderNumber: string;
  ShipToAddress1: string;
  ShipToAddress2: string;
  ShipToCity: string;
  ShipToName: string;
  ShipToPostalCode: string;
  ShipToProvince: string;
  ShipVia: string;
  ShipViaDescription: string;
  SubTotal: number;
  TermsCode: string;
  VendorCode: string;
  VendorName: string;
  PartNumbers: IPurchaseOrderPartNumberResult[];
  Receipts: IReceiptResult[];
}

export interface IPurchaseOrderPartNumberResult {
  RandmarSKU: string;
  Description: string;
  Location: string;
  MPN: string;
  PurchaseOrderNumber: string;
  PurchaseOrderDate: number;
  QuantityOutstanding: number;
  UnitPrice: number;
  Category: string;
  LineNumber: number;
}

export interface IRequisitionPartNumberResult {
  RandmarSKU: string;
  Description: string;
  Location: string;
  MPN: string;
  RequisitionNumber: string;
  QuantityOrdered: number;
  UnitPrice: number;
}

export interface IReceiptPartNumberResult {
  RandmarSKU: string;
  Description: string;
  Location: string;
  MPN: string;
  ReceiptNumber: string;
  Quantity: number;
  UnitPrice: number;
}

export interface IReceiptResult {
  InvoiceNumber: string;
  ReceiptNumber: string;
  Location: string;
  VendorName: string;
  ReceiptDate: number;
  PurchaseOrderNumber: string;
  Comments: string;
  Orders: IOrderResult[];
  PartNumbers: IReceiptPartNumberResult[];
  Invoiced: boolean;
  SubTotal: number;
  TaxAmount1: number;
  TaxAmount2: number;
  Total: number;
}

export interface IInvoice {
  ManufacturerId: string;
  ManufacturerName: string;
  InvoiceDate: number;
  InvoiceNumber: string;
  PurchaseOrderNumber: string;
  DueDate: number;
  TaxAmount1: number;
  TaxRate1: number;
  TaxAmount2: number;
  TaxRate2: number;
  SubTotal: number;
  Total: number;
  Payed: boolean;
  DocumentType: string;
}

interface IOrderResult {
  Location: string;
  OrderDate: number;
  ShippingDate: number;
  OrderNumber: string;
  PONumber: string;
  ShipToName: string;
  ResellerName: string;
  ResellerId: string;
  ShipVia: string;
  ShipViaDescription: string;
  PrintStatus: number;
  Priority: string;
  NumberOfShipments: number;
  NumberOfLines: number;
  Comments: string;
  SubTotal: number;
}

export interface IOrderDetailResult {

  Category?: string;
  DocumentDate?: number;
  OrderDate?: number;
  DocumentNumber?: string;
  DocumentType?: string;
  ExtendedPrice?: number;
  ExtendedWeight?: number;
  MPN?: string;
  ManufacturerId?: string;
  OpportunityManufacTurerId?: string;
  OpportunityManufacturerNumber?: string;
  OrderNumber?: string;
  PONumber?: string;
  ManufacturerName?: string;
  Quantity?: number;
  RandmarSKU?: string;
  ResellerId?: string;
  ResellerName?: string;
  ShipToName?: string;
  ShipToZipCode?: string;
  ShipViaDescription?: string;
  UnitPrice?: number;
  UnitWeight?: number;
  WarehouseCode?: string;
}

export interface IGeneralDocument {
  AllowPartialShipment: boolean;
  BidNumber: string;
  EndUserPO: string;
  EnteredBy: string;
  ManufacturerId: string;
  OrderDate: string;
  ActiveOrderDetails: IOrderDetailResult[];
  CompletedSalesOrderDetails: IOrderDetailResult[];
  Reseller: IResellerInfo;
  OrderNumber: string;
  PONumber: string;
  ResellerId: number;
  ShipAddress1: string;
  ShipAddress2: string;
  ShipCity: string;
  ShipContact: string;
  ShipCountry: string;
  ShipEmail: string;
  ShipName: string;
  ShippingSlipComment: string;
  ShipProvince: string;
  ShipViaCode: string;
  ShipZip: string;
}
